import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

interface DashboardCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  count?: number;
}

interface Course {
  id: string;
  title: string;
  status: string;
  level: string;
  subject: string;
}

interface Class {
  id: string;
  title: string;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  enrollmentCount: number;
}

interface Certification {
  id: string;
  name: string;
  type: 'aviation_security' | 'iata' | 'icao' | 'customer_service' | 'technical';
  issuedCount: number;
  validCount: number;
  expiringCount: number;
  expiryDate: string;
  branch: string;
  status: 'active' | 'expiring' | 'expired';
}

const LMSDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();
  
  // Define typed state variables
  const [courses, setCourses] = useState<Course[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);

  useEffect(() => {
    // Simulate data loading
    const loadData = async () => {
      setIsLoading(true);
      
      // In a real implementation, you would fetch data from an API
      setTimeout(() => {
        setCourses([
          {
            id: 'AGH001',
            title: 'An toàn Hàng không Cơ bản',
            status: 'published',
            level: 'Cơ bản',
            subject: 'An toàn & Bảo mật',
          },
          {
            id: 'AGH002',
            title: 'Vận hành Thiết bị Mặt đất (GSE)',
            status: 'published',
            level: 'Trung cấp',
            subject: 'Thiết bị & Kỹ thuật',
          },
          {
            id: 'AGH003',
            title: 'Xử lý Hành lý và Cargo',
            status: 'draft',
            level: 'Cơ bản',
            subject: 'Dịch vụ Khách hàng',
          },
          {
            id: 'AGH004',
            title: 'Điều hướng Tàu bay (Aircraft Marshalling)',
            status: 'published',
            level: 'Nâng cao',
            subject: 'Điều hành Bay',
          },
          {
            id: 'AGH005',
            title: 'Phục vụ Hành khách Đặc biệt (PRM)',
            status: 'pending_review',
            level: 'Trung cấp',
            subject: 'Dịch vụ Khách hàng',
          },
          {
            id: 'AGH006',
            title: 'Ứng phó Tình huống Khẩn cấp',
            status: 'published',
            level: 'Bắt buộc',
            subject: 'An toàn & Bảo mật',
          },
          {
            id: 'FLIGHT001',
            title: 'Tiếng Anh Hàng không Chuyên nghiệp',
            status: 'published',
            level: 'Trung cấp',
            subject: 'Ngôn ngữ & Giao tiếp',
          },
          {
            id: 'MAINT001',
            title: 'Bảo trì Thiết bị Sân bay',
            status: 'draft',
            level: 'Chuyên gia',
            subject: 'Thiết bị & Kỹ thuật',
          }
        ]);

        setClasses([
          {
            id: 'HAN001',
            title: 'An toàn Hàng không - Chi nhánh Hà Nội',
            status: 'active',
            startDate: '2025-03-15',
            endDate: '2025-04-15',
            enrollmentCount: 28,
          },
          {
            id: 'SGN001',
            title: 'Vận hành GSE - Chi nhánh TP.HCM',
            status: 'upcoming',
            startDate: '2025-04-01',
            endDate: '2025-05-15',
            enrollmentCount: 24,
          },
          {
            id: 'DAD001',
            title: 'Phục vụ PRM - Chi nhánh Đà Nẵng',
            status: 'active',
            startDate: '2025-03-20',
            endDate: '2025-04-20',
            enrollmentCount: 18,
          },
          {
            id: 'HAN002',
            title: 'Ứng phó Khẩn cấp - Chi nhánh Hà Nội',
            status: 'upcoming',
            startDate: '2025-04-10',
            endDate: '2025-04-25',
            enrollmentCount: 32,
          },
          {
            id: 'SGN002',
            title: 'Tiếng Anh Hàng không - Chi nhánh TP.HCM',
            status: 'active',
            startDate: '2025-03-10',
            endDate: '2025-06-10',
            enrollmentCount: 45,
          },
          {
            id: 'CXR001',
            title: 'Điều hướng Tàu bay - Chi nhánh Cam Ranh',
            status: 'completed',
            startDate: '2025-02-15',
            endDate: '2025-03-15',
            enrollmentCount: 16,
          },
          {
            id: 'HPH001',
            title: 'Xử lý Hành lý - Chi nhánh Hải Phòng',
            status: 'upcoming',
            startDate: '2025-04-15',
            endDate: '2025-05-15',
            enrollmentCount: 22,
          },
          {
            id: 'PQC001',
            title: 'Bảo trì Thiết bị - Chi nhánh Phú Quốc',
            status: 'upcoming',
            startDate: '2025-04-20',
            endDate: '2025-06-20',
            enrollmentCount: 14,
          }
        ]);

        setCertifications([
          {
            id: 'CERT001',
            name: 'Chứng chỉ An ninh Hàng không Cấp 3',
            type: 'aviation_security',
            issuedCount: 856,
            validCount: 743,
            expiringCount: 87,
            expiryDate: '2025-02-15',
            branch: 'Nội Bài - Hà Nội',
            status: 'expiring'
          },
          {
            id: 'CERT002',
            name: 'IATA Cargo Agent Training',
            type: 'iata',
            issuedCount: 345,
            validCount: 312,
            expiringCount: 23,
            expiryDate: '2025-03-20',
            branch: 'Tân Sơn Nhất - TP.HCM',
            status: 'active'
          },
          {
            id: 'CERT003',
            name: 'ICAO Air Traffic Management',
            type: 'icao',
            issuedCount: 156,
            validCount: 134,
            expiringCount: 12,
            expiryDate: '2025-04-10',
            branch: 'Đà Nẵng',
            status: 'active'
          },
          {
            id: 'CERT004',
            name: 'Dịch vụ Khách hàng Chuyên nghiệp',
            type: 'customer_service',
            issuedCount: 654,
            validCount: 589,
            expiringCount: 45,
            expiryDate: '2025-03-05',
            branch: 'Cam Ranh - Khánh Hòa',
            status: 'active'
          },
          {
            id: 'CERT005',
            name: 'Vận hành & Bảo trì Radar',
            type: 'technical',
            issuedCount: 98,
            validCount: 87,
            expiringCount: 8,
            expiryDate: '2025-05-15',
            branch: 'Phú Quốc - Kiên Giang',
            status: 'active'
          }
        ]);
        
        setIsLoading(false);
      }, 1000);
    };

    loadData();
  }, []);

  if (isLoading) {
    return <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">{t('lms.dashboard.loading')}</div>;
  }

  const totalCertifications = certifications.reduce((sum, cert) => sum + cert.issuedCount, 0);
  const totalValid = certifications.reduce((sum, cert) => sum + cert.validCount, 0);
  const totalExpiring = certifications.reduce((sum, cert) => sum + cert.expiringCount, 0);

  const dashboardCards: DashboardCard[] = [
    {
      title: t('lms.dashboard.allCourses.title'),
      description: t('lms.dashboard.allCourses.description'),
      href: '/lms/courses',
      count: courses.length,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      title: t('lms.dashboard.activeClasses.title'),
      description: t('lms.dashboard.activeClasses.description'),
      href: '/lms/classes',
      count: classes.filter(c => c.status === 'active').length,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      title: 'Chứng chỉ Đào tạo',
      description: 'Quản lý chứng chỉ và cấp phép nhân viên',
      href: '/lms/certifications',
      count: totalValid,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      )
    },
    {
      title: t('lms.dashboard.createCourse.title'),
      description: t('lms.dashboard.createCourse.description'),
      href: '/lms/courses/new',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const recentCourses = courses.slice(0, 4);
  const recentClasses = classes.slice(0, 4);
  const recentCertifications = certifications.slice(0, 5);

  const getClassStatusIndicator = (status: string) => {
    switch (status) {
      case 'upcoming':
        return <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>;
      case 'active':
        return <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>;
      case 'completed':
        return <span className="inline-block w-2 h-2 bg-gray-500 rounded-full mr-2"></span>;
      case 'cancelled':
        return <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>;
      default:
        return null;
    }
  };

  const getCertificationStatusIndicator = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>;
      case 'expiring':
        return <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>;
      case 'expired':
        return <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>;
      default:
        return null;
    }
  };

  const getCertificationStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Hiệu lực';
      case 'expiring': return 'Sắp hết hạn';
      case 'expired': return 'Hết hạn';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCourseStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return t('lms.courseStatus.draft');
      case 'pending_review':
        return t('lms.courseStatus.pending_review');
      case 'published':
        return t('lms.courseStatus.published');
      default:
        return status;
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full px-6 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">{t('lms.dashboard.title')}</h1>
          <p className="text-gray-600">Hệ thống Dịch vụ Sân bay - Quản lý đào tạo toàn diện</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardCards.map((card, index) => (
            <Link key={index} to={card.href} className="block">
              <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-center mb-4">
                  <div className="p-3 bg-blue-100 text-blue-800 rounded-lg">
                    {card.icon}
                  </div>
                  {card.count !== undefined && (
                    <span className="text-2xl font-bold text-gray-900">{card.count}</span>
                  )}
                </div>
                <h2 className="text-lg font-semibold mb-2 text-gray-900">{card.title}</h2>
                <p className="text-gray-600">{card.description}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Certification Alert */}
        {totalExpiring > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Cảnh báo:</strong> Có {totalExpiring} chứng chỉ sắp hết hạn trong 30 ngày tới. 
                  <Link to="/lms/certifications" className="font-medium underline hover:text-yellow-800 ml-1">
                    Xem chi tiết →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('lms.dashboard.recentCourses')}</h2>
              <Link to="/lms/courses" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                {t('lms.dashboard.viewAll')}
              </Link>
            </div>
            
            {recentCourses.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('lms.dashboard.noCourses')}</p>
            ) : (
              <div className="space-y-3">
                {recentCourses.map(course => (
                  <Link key={course.id} to={`/lms/courses/${course.id}`} className="block">
                    <div className="p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100">
                      <div className="flex justify-between items-start">
                        <h3 className="font-medium text-gray-900 mb-1 text-sm">{course.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          course.status === 'published' ? 'bg-green-100 text-green-800' :
                          course.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getCourseStatusLabel(course.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {course.level} · {course.subject}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('lms.layout.activeClasses')}</h2>
              <Link to="/lms/classes" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                {t('lms.dashboard.manageClasses')}
              </Link>
            </div>
            
            {recentClasses.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('lms.dashboard.noClasses')}</p>
            ) : (
              <div className="space-y-3">
                {recentClasses.map(classItem => (
                  <Link key={classItem.id} to={`/lms/classes/${classItem.id}`} className="block">
                    <div className="p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium flex items-center text-gray-900 text-sm">
                          {getClassStatusIndicator(classItem.status)}
                          {classItem.title}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {classItem.enrollmentCount} {t('lms.dashboard.students')}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(classItem.startDate)} - {formatDate(classItem.endDate)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Chứng chỉ Hiện tại</h2>
              <Link to="/lms/certifications" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Quản lý
              </Link>
            </div>
            
            {recentCertifications.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Chưa có chứng chỉ nào</p>
            ) : (
              <div className="space-y-3">
                {recentCertifications.map(cert => (
                  <Link key={cert.id} to={`/lms/certifications/${cert.id}`} className="block">
                    <div className="p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium flex items-center text-gray-900 text-sm">
                          {getCertificationStatusIndicator(cert.status)}
                          {cert.name}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          cert.status === 'active' ? 'bg-green-100 text-green-800' :
                          cert.status === 'expiring' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {getCertificationStatusText(cert.status)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mb-1">📍 {cert.branch}</div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{cert.validCount}/{cert.issuedCount} hiệu lực</span>
                        <span>{cert.expiringCount} sắp hết hạn</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LMSDashboard; 